/**
 * TextRuleRow - Einzelne Text-Regel Zeile mit Inline-Editing
 *
 * Klick auf den Wert oeffnet ein Eingabefeld zum Aendern.
 */

import { useState, useRef, useEffect } from 'react'
import { Pencil, Trash2, Check, X } from 'lucide-react'
import { Button } from '@/components/v2/ui'

const ATTRIBUTE_LABELS: Record<string, string> = {
  title: 'Titel',
  description: 'Beschreibung',
  short_description: 'Kurzbeschreibung',
  bullets: 'Bullet Points',
  seo_title: 'SEO Titel',
  seo_description: 'SEO Beschreibung',
}

const RULE_TYPE_LABELS: Record<string, string> = {
  min_length: 'Min. Zeichen',
  max_length: 'Max. Zeichen',
  required: 'Pflichtfeld',
  min_count: 'Min. Anzahl',
  max_item_length: 'Max. Zeichen/Item',
}

interface TextRuleRowProps {
  rule: {
    id: string
    attribute_code: string
    rule_type: string
    rule_config: { value: number | boolean }
  }
  onDelete: (ruleId: string) => void
  onSave: (rule: { attribute_code: string; rule_type: string; rule_config: { value: number | boolean } }) => void
  isDeleting?: boolean
  isSaving?: boolean
}

export function TextRuleRow({ rule, onDelete, onSave, isDeleting, isSaving }: TextRuleRowProps) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const isBoolean = typeof rule.rule_config.value === 'boolean'

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  const startEdit = () => {
    if (isBoolean) return
    setEditValue(String(rule.rule_config.value))
    setEditing(true)
  }

  const confirmEdit = () => {
    const parsed = parseInt(editValue, 10)
    if (isNaN(parsed) || parsed < 0) return
    onSave({
      attribute_code: rule.attribute_code,
      rule_type: rule.rule_type,
      rule_config: { value: parsed },
    })
    setEditing(false)
  }

  const cancelEdit = () => setEditing(false)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') confirmEdit()
    if (e.key === 'Escape') cancelEdit()
  }

  const attrLabel = ATTRIBUTE_LABELS[rule.attribute_code] || rule.attribute_code
  const typeLabel = RULE_TYPE_LABELS[rule.rule_type] || rule.rule_type

  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
      <div className="flex items-center gap-4 min-w-0">
        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 w-36 truncate">
          {attrLabel}
        </span>
        <span className="text-sm text-neutral-500 dark:text-neutral-400 w-32">
          {typeLabel}
        </span>

        {editing ? (
          <div className="flex items-center gap-1.5">
            <input
              ref={inputRef}
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-20 text-sm font-mono rounded border border-primary-400 dark:border-primary-600 bg-white dark:bg-neutral-800 px-2 py-1"
              min={0}
            />
            <Button variant="ghost" size="sm" onClick={confirmEdit} disabled={isSaving} className="h-7 w-7 p-0 text-green-600">
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-7 w-7 p-0 text-neutral-400">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <button
            onClick={startEdit}
            disabled={isBoolean}
            className="flex items-center gap-1.5 group"
          >
            <span className="text-sm font-mono text-neutral-700 dark:text-neutral-300">
              {isBoolean ? (rule.rule_config.value ? 'Ja' : 'Nein') : rule.rule_config.value}
            </span>
            {!isBoolean && (
              <Pencil className="h-3 w-3 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </button>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete(rule.id)}
        disabled={isDeleting}
        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}
